import React from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import {
  colors,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../../theme';
import LogoMark from '../LogoMark';
import LogoWordmark from '../LogoWordmark';
import Icon, { IconName } from '../ui/Icon';
import Text from '../ui/Text';

/** Height shared by the brand lockup and the action glyphs. */
const ROW_HEIGHT = 32;

type ActionProps = {
  icon: IconName;
  label: string;
  onPress?: () => void;
  /** Shown as a count bubble; omitted or 0 renders nothing. */
  badge?: number;
};

function Action({ icon, label, onPress, badge = 0 }: ActionProps) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={badge > 0 ? `${label}, ${badge} items` : label}
      onPress={onPress}
      // The box is the glyph's own size, so it lines up with the logo; the
      // slop restores a 44pt+ target without adding height to the row.
      hitSlop={10}
      style={({ pressed }) => [styles.action, pressed ? styles.pressed : null]}
    >
      <Icon name={icon} size={26} color={colors.ink} strokeWidth={1.7} />
      {badge > 0 ? (
        <View style={styles.badge}>
          <Text variant="caption" color={colors.onPrimary} style={styles.badgeText}>
            {badge > 9 ? '9+' : badge}
          </Text>
        </View>
      ) : null}
    </Pressable>
  );
}

type Props = {
  address: string;
  onPressAddress?: () => void;
  onPressNotifications?: () => void;
  /**
   * The wallet, not the cart - the cart is its own bottom tab now, with its
   * own badge there (see `BottomNav`), so this header no longer needs to
   * carry a second copy of the same count.
   */
  onPressWallet?: () => void;
  /** Unread notifications; shown as a count bubble on the bell. */
  notificationCount?: number;
};

/**
 * One row: where the work happens, the brand, and the account actions.
 *
 * The logo is centred by giving the address and the actions equal flex, so the
 * lockup lands on the true middle of the row whatever the address says. An
 * absolutely-positioned centre would have sat on top of a long address instead
 * of pushing it.
 */
function HomeHeader({
  address,
  onPressAddress,
  onPressNotifications,
  onPressWallet,
  notificationCount = 0,
}: Props) {
  const resp = useResponsive();

  /*
   * On desktop web both actions live in the hamburger menu instead - see
   * `BottomNav`'s `webDesktop` - so the corner is left to the menu button
   * that floats there. Rendered as an empty flank rather than dropped, so
   * the brand keeps its centring (the two `side` views must stay equal).
   */
  const webDesktop = Platform.OS === 'web' && resp.isTablet;

  return (
    <View style={styles.header}>
      {/* Truncates rather than growing. It shares the row with the brand now,
          so a long address has to give way instead of pushing it off-centre. */}
      <View style={styles.side}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Serving ${address}. Change location`}
          onPress={onPressAddress}
          style={({ pressed }) => [styles.address, pressed ? styles.pressed : null]}
        >
          <Icon name="pin" size={16} color={colors.primary} />
          <Text variant="bodySm" numberOfLines={1} style={styles.addressText}>
            {address}
          </Text>
          <Icon name="chevronDown" size={14} color={colors.inkMuted} />
        </Pressable>
      </View>

      {/* Mark and wordmark side by side. The stacked lockup is too tall for a
          header row, so the symbol sits to the left of the type instead. */}
      <View
        style={styles.brand}
        accessible
        accessibilityRole="image"
        accessibilityLabel="Homingo"
      >
        <LogoMark size={25} color={colors.primary} />
        <LogoWordmark height={20} />
      </View>

      <View style={[styles.side, styles.sideEnd]}>
        {webDesktop ? null : (
          <>
            <Action
              icon="bell"
              label="Notifications"
              onPress={onPressNotifications}
              badge={notificationCount}
            />
            {/*
              The glyph only - no balance beside it.

              It used to carry the figure in a pill, on the reasoning that the
              balance is what people open the wallet to find out. In practice
              the number is the wrong thing for a header to assert: it is
              `undefined` until the summary request lands, so the first paint
              showed a "-" that reads as an empty wallet, and the pill changed
              width when the real figure arrived and shifted the row. A glyph
              says the same thing without either problem, and the balance is
              the first thing on the page it opens.
            */}
            <Action icon="wallet" label="Wallet" onPress={onPressWallet} />
          </>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    paddingBottom: spacing.xs,
    gap: spacing.sm,
  },
  /*
   * Equal halves either side of the lockup. This is what centres it: the two
   * flanks claim the same width whatever is inside them, so the brand sits on
   * the row's true middle rather than wherever the address happens to end.
   */
  side: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  sideEnd: {
    justifyContent: 'flex-end',
  },
  /*
   * Fixed to the glyph height the actions use, so the brand and the two icons
   * share one band and start at the same y. The mark is 22pt and the icons 26,
   * so without this the logo sat a few points higher than they did.
   */
  brand: {
    flexDirection: 'row',
    alignItems: 'center',
    height: ROW_HEIGHT,
    gap: spacing.xs,
    flexShrink: 0,
    /*
     * Nudged off the geometric centre.
     *
     * The flanks are equal, so the lockup was mathematically centred - but the
     * left one is a solid run of text and the right two glyphs with air around
     * them, which pulls the apparent middle leftward. This puts it back.
     */
    marginLeft: spacing.md,
  },
  address: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
    gap: 2,
  },
  addressText: {
    flexShrink: 1,
  },
  /*
   * Sized to the glyph, not to the touch target - a 42pt box centred its icon
   * 8pt below the logo beside it, which is what made the two look unevenly
   * padded. `hitSlop` on the Pressable covers the target instead, and costs no
   * layout height, so the search field below does not move.
   */
  action: {
    width: 32,
    height: ROW_HEIGHT,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressed: {
    opacity: 0.6,
  },
  badge: {
    position: 'absolute',
    top: 0,
    right: 0,
    minWidth: 15,
    height: 15,
    paddingHorizontal: 3,
    borderRadius: radii.pill,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    fontSize: 10,
    lineHeight: 13,
  },
});

/* Memoised: it sits inside the hero, which re-renders on every banner tick. */
export default React.memo(HomeHeader);
